import { type InputNumberProps } from 'antd';
import { useField } from 'formik';
import { isNil } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type CompanyContextDataFragment } from '../../api/fragments/CompanyContextData';
import useFormats, { withCommasForNumberString } from '../../utilities/useFormats';
import FormItem from './FormItem';
import { InputNumber, type InputFieldProps } from './InputNumberField';

interface CompanyCurrencyFieldProps extends InputFieldProps {
    company?: Pick<CompanyContextDataFragment, 'currency' | 'roundings'>;
}

// Basically is the same as input number field
// but it took company formats into account
// and since the currency only related to company, it's okay to use it as props
const CompanyCurrencyField = ({ company, name, required, label, itemProps, ...props }: CompanyCurrencyFieldProps) => {
    const [field, meta, { setValue }] = useField({ name });

    const { formatAmount, roundAmount } = useFormats(
        company?.currency || '',
        company?.roundings?.amount?.decimals || 0,
        company?.roundings?.percentage?.decimals || 0
    );

    const formatter = useCallback(
        (value, { userTyping }) => {
            if (!value) {
                return value;
            }

            return userTyping ? withCommasForNumberString(value) : formatAmount(value);
        },
        [formatAmount]
    );

    const given = useMemo(() => {
        if (!isNil(field.value)) {
            return field.value;
        }

        return undefined;
    }, [field.value]);

    const [representation, setRepresentation] = useState<number | undefined>(given);

    useEffect(() => {
        if (!isNil(field.value)) {
            setRepresentation(field.value);
        }
    }, [field.value]);

    const onChange = useCallback<InputNumberProps['onChange']>(
        (value: number) => {
            setRepresentation(value);
        },
        [setRepresentation]
    );

    const onBlur = useCallback<InputNumberProps['onBlur']>(() => {
        if (isNil(representation)) {
            setRepresentation(given);

            return;
        }

        setValue(roundAmount(representation));
    }, [given, representation, roundAmount, setValue]);

    const onFocus = useCallback<InputNumberProps['onFocus']>(() => {
        setRepresentation(undefined);
    }, [setRepresentation]);

    return (
        <FormItem {...itemProps} label={label} meta={meta} required={required}>
            <InputNumber
                autoComplete="off"
                // spread props
                {...props}
                // then spread the field properties itself
                {...field}
                // manually attach on change
                $offsetTop="1px"
                controls={false}
                formatter={formatter}
                onBlur={onBlur}
                onChange={onChange}
                onFocus={onFocus}
                prefix={company?.currency}
                value={representation}
            />
        </FormItem>
    );
};

export default CompanyCurrencyField;
